/* eslint-disable max-lines */

import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'

import { CreditCardFeatureFilterOptionsValues, CreditCardFilterTypes, PersonalizedStatuses } from 'embed/constants'
import { CreditOffer, CreditOfferDetails } from 'embed/constants/models'
import { Product, TrackingKey } from 'even-ts-static'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { Timer } from 'embed/components/Timer'
import { ConnectedCreditOfferListContainer } from '../CreditOfferList/container'

import { CreditFilterCSTEventData } from 'common/actions'
import { capitalize, getContrastingColor, pluralize } from 'embed/utils'
import OnceLoaded from '../../OnceLoaded'
import ProgressRing from '../../ProgressRing'
import { ButtonToggle } from '../../Toggles/ButtonToggle'
import { CreditOfferListItem } from '../CreditOfferList/ListItem/list-item'
import { ResultsFilters } from './ResultsFilters'
import { applicationCreditCardFirstNameSelector } from 'embed/selectors/application'
import { CommonState } from 'embed/reducers'
import { configAllowPersonalizedLinkoutSelector } from 'ui/multi-product/selectors/config'
import { filterUniqueOffersByUuid } from 'common/services/OfferFilters'

const styles = require('./styles.scss')
const DoubleChevron = require('./double-chevron.svg')

export type FiltersState = Readonly<{ [key in CreditCardFilterTypes]?: ButtonToggle[] }>

const initialFiltersState: FiltersState = {
  [CreditCardFilterTypes.CARD_FEATURE]: [],
  [CreditCardFilterTypes.CARD_TYPE]: [],
}

export const getActiveFilters = (filters: ButtonToggle[] = []) => filters?.filter((filter) => filter.active)

export const doesOfferContainCardPurpose = (purpose: string, offer: CreditOffer) =>
  offer.details.cardPurposes?.includes(purpose)

const checkDetailsForFeature = (featureType: CreditCardFeatureFilterOptionsValues, details: CreditOfferDetails) => {
  const doesOfferHaveZeroApr = () => details.minPurchaseIntroApr === 0 || details.maxPurchaseIntroApr === 0
  const doesOfferHaveNoAnnualFee = () => details.minAnnualFee === 0 && details.maxAnnualFee === 0
  const doesOfferHaveNoForeignTransactionFee = () => details.foreignExchangeFee === 0
  const doesOfferHaveIntroBonusOffer = () => details.introOfferAmount && details.introOfferAmount > 0

  const features = {
    [CreditCardFeatureFilterOptionsValues.ZERO_APR]: doesOfferHaveZeroApr(),
    [CreditCardFeatureFilterOptionsValues.NO_ANNUAL_FEE]: doesOfferHaveNoAnnualFee(),
    [CreditCardFeatureFilterOptionsValues.NO_FOREIGN_TRANSACTION_FEE]: doesOfferHaveNoForeignTransactionFee(),
    [CreditCardFeatureFilterOptionsValues.INTRO_BONUS_OFFER]: doesOfferHaveIntroBonusOffer(),
  }

  return features[featureType]
}

export const doesOfferQualifyForFeature = (
  cardFeaturesToFilter: CreditCardFeatureFilterOptionsValues[],
  offer: CreditOffer,
): boolean => {
  // based on the active feature filters, does the card offer
  // meet the criteria to remain shown to the user
  const { details } = offer
  const doesOfferQualifyForFeatures: (number | boolean | undefined)[] = []
  for (const feature of cardFeaturesToFilter) {
    doesOfferQualifyForFeatures.push(checkDetailsForFeature(feature, details))
  }

  return doesOfferQualifyForFeatures.every((feature) => !!feature)
}

// If the offer meets the card purposes and features filters, we want to push them to the filtered results list
export const doesOfferPassFilters = (
  cardPurposesToFilter: string[],
  cardFeaturesToFilter: CreditCardFeatureFilterOptionsValues[],
  offer: CreditOffer,
) =>
  cardPurposesToFilter.every((purpose) => doesOfferContainCardPurpose(purpose, offer)) &&
  doesOfferQualifyForFeature(cardFeaturesToFilter, offer)

interface FilteredOffers {
  otherOffers: CreditOffer[]
  personalizedOffers: CreditOffer[]
}

interface OwnProps {
  allCreditCardOffers?: CreditOffer[]
  cardPurposesValue?: string
  cardTypeFilters: ButtonToggle[]
  cardFeatureFilters: ButtonToggle[]
  handleTrackingEvent: (trackingKey: TrackingKey, data?: CreditFilterCSTEventData) => void
  onOfferClick: (offer: CreditOffer) => void
  onTimerComplete: () => void
  toggleSpendingProfile?: (visible: boolean) => void
  otherOffers: CreditOffer[]
  personalizedOffers: CreditOffer[]
  rateTableLeadUuid: string
  rateTableUuid: string
  rewardsEnabled?: boolean
  selectedOffer?: CreditOffer
  shouldAutoRedirect: boolean
}

interface State {
  allowPersonalizedLinkout: boolean
  firstName?: string
}

type Props = OwnProps & State

// eslint-disable-next-line complexity
const Render: React.SFC<Props> = (props) => {
  const {
    allCreditCardOffers,
    allowPersonalizedLinkout,
    cardTypeFilters,
    cardFeatureFilters,
    handleTrackingEvent,
    otherOffers,
    personalizedOffers,
    onOfferClick,
    onTimerComplete,
    toggleSpendingProfile,
    firstName,
    selectedOffer,
    shouldAutoRedirect,
    rateTableUuid,
    rateTableLeadUuid,
    rewardsEnabled,
  } = props

  // state for showing/hiding the filters container
  const [isFilterContainerVisible, setIsFilterContainerVisible] = React.useState(false)

  // sttate for filtered offers
  const [filteredOffers, setFilteredOffers] = React.useState<FilteredOffers>({
    otherOffers: [...otherOffers],
    personalizedOffers: [...personalizedOffers],
  })

  // state for the unapplied filtered offers
  const [preAppliedFilteredOffers, setPreAppliedFilteredOffers] = React.useState<FilteredOffers>({
    otherOffers: [...filteredOffers?.otherOffers],
    personalizedOffers: [...filteredOffers?.personalizedOffers],
  })

  // state for when the user toggles filters but doesn't apply or clear before closing the
  // filters container
  const [resetState, setResetState] = React.useState(initialFiltersState)

  // state for which filters are active/inactive
  const [filtersState, setFiltersState] = React.useState<FiltersState>(initialFiltersState)

  // state for the number of active filters for the toggle badge
  const [numberOfActiveFilters, setNumberOfActiveFilters] = React.useState<number | null>(null)

  // Create a reference and when the reference exists
  // set the available filters and the initial reset state
  const ref = React.useRef<Props | undefined>(undefined)
  React.useEffect(() => {
    const state: FiltersState = {
      [CreditCardFilterTypes.CARD_FEATURE]: cardFeatureFilters,
      [CreditCardFilterTypes.CARD_TYPE]: cardTypeFilters,
    }
    setFiltersState(state)
    setResetState(state)
    filterOffers(true, true)
  }, [ref.current])

  // When the sort order of the supplied personalized and other offers changes
  // Apply any/all filters
  // Uuids have to be used here because of the possiblity that viewport update
  // Is causing re-rendering.  When you scroll, personalizedOffers and otherOffers
  // are being updated from the parent so they cannot be used as dependencies here
  // because scrolling will trigger this effect and filter the offers

  // Capture when sort order changes of the supplied personalized/other offers
  const allOffers = personalizedOffers?.concat(otherOffers)
  const offerUuids = allOffers.map((offer) => offer.uuid).join(',')

  React.useEffect(() => {
    filterOffers(true)
    filterOffers(false, true)
  }, [offerUuids])

  // an effect for getting the number of filtered offers for the show me button
  React.useEffect(() => {
    filterOffers(false, true)
  }, [filtersState])

  const resetFilters = () => {
    const allFilters = Object.values(filtersState).reduce((final, group: ButtonToggle[]) => final?.concat(group))

    allFilters?.filter((filter) => filter.active).forEach((filter) => toggleFilter(filter))
    filterOffers()
    setNumberOfActiveFilters(0)
    toggleFilterContainer()
  }

  // filter the offers
  // if the user has changed filters, without clicking clear or apply and then the user clicks to sort
  // we don't want to use the current state of the filters we want to use the reset state

  const filterOffers = (useResetState = false, preview = false) => {
    let updatedPersonalizedOffers: CreditOffer[] = []
    let updatedOtherOffers: CreditOffer[] = []
    const applicableState = useResetState ? resetState : filtersState
    const activeCardTypeFilters = getActiveFilters(applicableState[CreditCardFilterTypes.CARD_TYPE])
    const activeCardFeatureFilters = getActiveFilters(applicableState[CreditCardFilterTypes.CARD_FEATURE])

    // if the card purpose that we are filtering by is contained in the
    // offer card purposes array, we want to continue to show it
    // for both personalized and other offers

    const cardPurposesToFilter = activeCardTypeFilters?.map((filter) => filter.value)
    const cardFeaturesToFilter = activeCardFeatureFilters?.map(
      (filter) => filter.value as CreditCardFeatureFilterOptionsValues,
    )

    updatedPersonalizedOffers = personalizedOffers.filter((offer) =>
      doesOfferPassFilters(cardPurposesToFilter, cardFeaturesToFilter, offer),
    )
    updatedOtherOffers = otherOffers?.filter((offer) =>
      doesOfferPassFilters(cardPurposesToFilter, cardFeaturesToFilter, offer),
    )

    // if the offer meets the rules of the card features we are filtering by
    // we want to continue to show it for both personalized and other offers

    if (activeCardFeatureFilters?.length === 0 && activeCardTypeFilters?.length === 0) {
      // If no card filters are active, show the original list
      updatedPersonalizedOffers = personalizedOffers ?? []
      updatedOtherOffers = otherOffers ?? []
    }

    // Since it's possible that the same offer could be added by both filter types
    // Only set 1 instance of the offer in the filtered list

    if (preview) {
      setPreAppliedFilteredOffers({
        otherOffers: filterUniqueOffersByUuid(updatedOtherOffers) as CreditOffer[],
        personalizedOffers: filterUniqueOffersByUuid(updatedPersonalizedOffers) as CreditOffer[],
      })
    } else {
      setFilteredOffers({
        otherOffers: filterUniqueOffersByUuid(updatedOtherOffers) as CreditOffer[],
        personalizedOffers: filterUniqueOffersByUuid(updatedPersonalizedOffers) as CreditOffer[],
      })
    }
  }

  const toggleFilterContainer = (shouldReset = false) => {
    // if the filter is closed, capture the current filterstate and set it as the
    // reset state.  If the filter is closed without clicking clear or done
    // reset the filters to the state they were in when it was opened
    if (!isFilterContainerVisible) {
      setResetState({ ...filtersState })
      handleTrackingEvent(TrackingKey.CreditCardFilterExpanded)
    }
    if (shouldReset && isFilterContainerVisible) {
      setFiltersState(resetState)
      setResetState(resetState)
      handleTrackingEvent(TrackingKey.CreditCardFilterClosed)
    }
    setIsFilterContainerVisible(!isFilterContainerVisible)
  }

  const onFilterApply = () => {
    const activeCardTypeFilters = getActiveFilters(filtersState[CreditCardFilterTypes.CARD_TYPE]).map(
      (filter) => filter.label,
    )
    const activeCardFeatureFilters = getActiveFilters(filtersState[CreditCardFilterTypes.CARD_FEATURE]).map(
      (filter) => filter.label,
    )

    const eventData: CreditFilterCSTEventData = {
      activeCardFeatureFilters,
      activeCardTypeFilters,
      rateTableUuid,
    }

    handleTrackingEvent(TrackingKey.CreditCardFiltersApplied, eventData)

    // filter the offers, set the next reset state, set the number of active filters for the badge
    // and close the filter container
    filterOffers()
    setResetState(filtersState)
    setNumberOfActiveFilters(activeCardTypeFilters.concat(activeCardFeatureFilters)?.length)
    toggleFilterContainer()
  }

  const toggleFilter = (toggle: ButtonToggle) => {
    let applicableFilters: ButtonToggle[] | undefined
    let filterType: CreditCardFilterTypes

    let nextState = { ...filtersState }

    // based on the category of the filter, select the correct
    // filter set and state update function
    switch (toggle.filterCategory) {
      case CreditCardFilterTypes.CARD_TYPE:
        applicableFilters = nextState[CreditCardFilterTypes.CARD_TYPE]
        filterType = CreditCardFilterTypes.CARD_TYPE
        break
      case CreditCardFilterTypes.CARD_FEATURE:
        applicableFilters = nextState[CreditCardFilterTypes.CARD_FEATURE]
        filterType = CreditCardFilterTypes.CARD_FEATURE
        break
      default:
        return
    }

    applicableFilters?.forEach((filter) => {
      if (filter.label === toggle.label) {
        filter.active = !filter.active
      }
    })
    nextState = {
      ...nextState,
      [filterType]: applicableFilters,
    }
    setFiltersState(nextState)
  }

  // The number of offers to show in the See Offers button
  const previewOfferCount =
    preAppliedFilteredOffers?.personalizedOffers?.length + preAppliedFilteredOffers?.otherOffers?.length

  // The number of original offers
  const unfilteredOfferCount = personalizedOffers?.length + otherOffers?.length

  // The offer that was selected, if one was
  const offerWasSelected = !!selectedOffer

  // Text for the apply button
  const applyButtonText = `See ${previewOfferCount} ${pluralize('Offer', previewOfferCount)}`

  // Was preApproved, preQualified, or preSelected === true in offer.details
  const selectedOfferIsPersonalized =
    offerWasSelected && PersonalizedStatuses.some((status) => selectedOffer?.details[status])

  // Are there any offers that have preApproved, preQualifed, or preSelected === true in offer.details?
  const personalizedOffersExist = filteredOffers?.personalizedOffers?.length > 0

  // Are there non-personalized offers
  const otherOffersExist = filteredOffers?.otherOffers?.length > 0

  const [wasOnOfferClicked, setWasOnOfferClicked] = React.useState(false)

  const timer = !wasOnOfferClicked && shouldAutoRedirect && selectedOfferIsPersonalized

  const onOfferClickEnhanced = (offer: CreditOffer) => {
    setWasOnOfferClicked(true)
    onOfferClick(offer)
  }

  let title

  // Show selected offer if an offer was selected and it's personalized
  const showSelectedOffer = offerWasSelected && selectedOfferIsPersonalized

  if (offerWasSelected) {
    if (selectedOfferIsPersonalized || personalizedOffersExist || otherOffersExist) {
      title =
        allowPersonalizedLinkout && firstName
          ? `Congratulations, ${capitalize(firstName)}! We've matched you with the following offers`
          : `Congratulations! We've matched you with the following offers`
    } else {
      title = 'Unfortunately, it looks like there are no offers available for you at this time.'
    }
  } else if (personalizedOffersExist || otherOffersExist) {
    // If no offer was selected but there are personalized offers OR
    // there are no personalized offers but there are other offers
    title =
      allowPersonalizedLinkout && firstName
        ? `Congratulations, ${capitalize(firstName)}! We've matched you with the following offers`
        : `Congratulations! We've matched you with the following offers`
  } else {
    // If there are no offers
    title = 'Unfortunately, it looks like there are no offers available for you at this time.'
  }

  let subtitle
  if (timer) {
    // If shouldAutoRedirect and the selected offer is personalized
    subtitle = 'We will redirect you to finish your offer application momentarily.'
  }

  const listOfOffers: JSX.Element[] = []
  if (personalizedOffersExist) {
    if (showSelectedOffer) {
      // If we're showing the personalized offer, then we need to delineate the other personalized offers from it
      listOfOffers.push(<h2 data-test-id="otherOffersTitle">Other personalized offers you may be interested in</h2>)
    }
    listOfOffers.push(
      <ConnectedCreditOfferListContainer
        allCreditCardOffers={allCreditCardOffers}
        applyNow={true}
        data-test-id="personalized_offers"
        rewardsEnabled={rewardsEnabled}
        hideSorting={false}
        toggleSpendingProfile={toggleSpendingProfile}
        filteredCreditCardOffers={filteredOffers.personalizedOffers}
        handleOfferCta={onOfferClickEnhanced}
        rateTableUuid={rateTableUuid}
        leadUuid={rateTableLeadUuid}
      />,
    )
  }
  if (otherOffersExist && (showSelectedOffer ? !personalizedOffersExist : personalizedOffersExist)) {
    // If other offers exist and we're showing the selected offer XOR other personalized offers, we want
    // to dilineate the other offers with a header
    listOfOffers.push(<h2 data-test-id="otherOffersTitle">Other offers you may be interested in</h2>)
  }
  if (otherOffersExist && !(showSelectedOffer && personalizedOffersExist)) {
    // If other offers exist, show them in a list that's sortable
    listOfOffers.push(
      <ConnectedCreditOfferListContainer
        allCreditCardOffers={allCreditCardOffers}
        data-test-id="other_offers"
        applyNow={true}
        hideSorting={personalizedOffersExist}
        rewardsEnabled={rewardsEnabled}
        filteredCreditCardOffers={filteredOffers.otherOffers}
        toggleSpendingProfile={toggleSpendingProfile}
        handleOfferCta={onOfferClickEnhanced}
        rateTableUuid={rateTableUuid}
        leadUuid={rateTableLeadUuid}
      />,
    )
  }

  return (
    <ThemeConsumer>
      {({
        backdropStyles,
        borderRadiusLarge,
        boxStyles,
        colorBorder,
        colorTextSecondary,
        colorPrimary,
        fontFamilyPrimary,
      }) => (
        // eslint-disable-next-line complexity
        <React.Fragment>
          {/* Header Info Box */}
          <OnceLoaded product={Product.CreditCard}>
            <div
              className={classnames(
                styles.filterContainer,
                css`
                  border-radius: ${borderRadiusLarge};
                `,
              )}
            >
              <div
                className={classnames(
                  styles.header,
                  css`
                    background-color: ${backdropStyles.backgroundColor};
                    border-color: ${colorBorder};
                    border-radius: ${borderRadiusLarge};
                  `,
                )}
                style={boxStyles}
              >
                <div className={classnames(styles.column)}>
                  <div data-test-id="resultsTitle">
                    <h2>{title}</h2>
                    <div className={classnames(styles.row)}>
                      {timer === true && (
                        <Timer onComplete={onTimerComplete} seconds={10} started={true}>
                          {({ secondsRemaining }) => (
                            <div
                              className={classnames(
                                styles.timerCount,
                                css`
                                  background: ${backdropStyles.backgroundColor};
                                `,
                              )}
                            >
                              <ProgressRing
                                className={styles.timerProgress}
                                percentRemaining={1 - secondsRemaining / 10}
                                progressColor={colorPrimary}
                                ringColor={colorBorder}
                              />
                              {secondsRemaining}
                            </div>
                          )}
                        </Timer>
                      )}
                      {subtitle && <h2 className={styles.subtitle}>{subtitle}</h2>}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={classnames(styles.resultsFiltersContainer, {
                  [css(`
                display: none;
              `)]: !isFilterContainerVisible,
                })}
              >
                {(filtersState[CreditCardFilterTypes.CARD_TYPE] ?? []).length > 0 && (
                  <ResultsFilters
                    visible={isFilterContainerVisible}
                    headerText="Card Type"
                    onFilterToggle={toggleFilter}
                    filterToggles={filtersState[CreditCardFilterTypes.CARD_TYPE] || []}
                  />
                )}
                {(filtersState[CreditCardFilterTypes.CARD_FEATURE] ?? []).length > 0 && (
                  <ResultsFilters
                    visible={isFilterContainerVisible}
                    headerText="Card Features"
                    onFilterToggle={toggleFilter}
                    filterToggles={filtersState[CreditCardFilterTypes.CARD_FEATURE] ?? []}
                  />
                )}
                <div className={styles.buttonRow}>
                  <button
                    onClick={resetFilters}
                    className={css`
                      background-color: #ffffff;
                      color: ${colorPrimary};
                    `}
                  >
                    Clear Filters
                  </button>
                  <button
                    disabled={previewOfferCount === 0}
                    className={css`
                      color: #ffffff;
                      background-color: ${colorPrimary};
                    `}
                    onClick={onFilterApply}
                  >
                    {previewOfferCount > 0 && <span>{applyButtonText}</span>}
                    {previewOfferCount === 0 && <span>No Offers Available</span>}
                  </button>
                </div>
              </div>
              {unfilteredOfferCount > 0 && (
                <button
                  className={classnames(
                    styles.profileToggle,
                    css`
                      color: ${colorTextSecondary};
                      font-family: ${fontFamilyPrimary};
                      svg path {
                        fill: ${colorTextSecondary};
                      }
                      &:hover {
                        color: ${colorTextSecondary};
                        svg path {
                          fill: ${colorTextSecondary};
                        }
                      }
                    `,
                  )}
                  data-test-id="filter_toggle"
                  onClick={toggleFilterContainer.bind(null, true)}
                >
                  {numberOfActiveFilters !== null && numberOfActiveFilters > 0 && (
                    <span
                      className={classnames(
                        styles.numberOfActiveFiltersBadge,
                        css`
                          background-color: ${colorPrimary};
                          color: ${getContrastingColor(colorPrimary)};
                        `,
                      )}
                    >
                      {numberOfActiveFilters}
                    </span>
                  )}
                  <span>Filter</span>
                  <DoubleChevron
                    className={classnames(styles.chevron, { [styles.chevronFlipped]: isFilterContainerVisible })}
                  />
                </button>
              )}
            </div>
            {showSelectedOffer && selectedOffer && (
              <React.Fragment>
                <div
                  className={classnames(
                    styles.selectedOffer,
                    css`
                      border-bottom-color: ${colorBorder};
                    `,
                  )}
                >
                  {/* If a user selected an offer (or pass in via QP) and is pre-qualified for this offer, display the offer here. */}
                  <CreditOfferListItem
                    applyNow={true}
                    toggleSpendingProfile={toggleSpendingProfile}
                    dispatchCreditCardOfferCta={onOfferClickEnhanced}
                    offer={selectedOffer}
                  />
                </div>
              </React.Fragment>
            )}
            {listOfOffers}
          </OnceLoaded>
        </React.Fragment>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: CommonState) => ({
  allowPersonalizedLinkout: configAllowPersonalizedLinkoutSelector(state),
  firstName: applicationCreditCardFirstNameSelector(state),
})

export const CreditCardResults = connect(mapStateToProps)(Render)
